import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, shareReplay, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import type { CrawlRunRef } from './url-run-comparison.service';

/**
 * The period every surface names — Living Brief LB1.
 * Backend: `GET /api/v1/domains/:domain/crawl-run-period`, which resolves the
 * pair through the same selector the dashboard movers use.
 *
 * The label is built here rather than on the server so the dashboard, Issues and
 * URL Detail render one string formatted in one locale. A period spelled two
 * ways reads as two periods, and the whole point of the pass is that Issues is
 * talking about the same window the dashboard just showed.
 */
export type CrawlRunPeriodState = 'compared' | 'first-run' | 'absent';

export interface CrawlRunPeriodPayload {
  scopeDomain: string;
  runsCaptured: number;
  state: CrawlRunPeriodState;
  currentRun: CrawlRunRef | null;
  previousRun: CrawlRunRef | null;
}

/** What a period looks like on screen. Absent periods have no view at all. */
export interface CrawlRunPeriodView {
  state: 'compared' | 'first-run';
  /** True only for a real pair — the gate on rendering anything "previous". */
  hasPair: boolean;
  previousDay: string | null;
  currentDay: string | null;
  /** `May 28, 2026 → Aug 2, 2026`, or null when either run has no date. */
  range: string | null;
  /** The one canonical sentence fragment: what the dashboard header says. */
  label: string;
}

/** Anything carrying a resolved pair: the period payload, or a movers payload. */
export interface CrawlRunPeriodSource {
  state: string;
  currentRun: CrawlRunRef | null;
  previousRun: CrawlRunRef | null;
}

export function formatCrawlRunDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Turn a resolved pair into the period label, or null when there is nothing
 * honest to say.
 *
 * A missing date degrades the label rather than faking one: "Since previous
 * crawl" with no parenthetical is true, and inventing a day from the run's
 * `startedAt` or today's date would not be. `state` is read first — an
 * `absent`/`incomparable` payload never produces a "previous" of any kind.
 */
export function buildCrawlRunPeriodView(
  source: CrawlRunPeriodSource | null | undefined
): CrawlRunPeriodView | null {
  if (!source) return null;

  const currentDay = formatCrawlRunDay(source.currentRun?.completedAt);

  if (source.state === 'compared' && source.previousRun) {
    const previousDay = formatCrawlRunDay(source.previousRun.completedAt);
    const range = previousDay && currentDay ? `${previousDay} → ${currentDay}` : null;
    return {
      state: 'compared',
      hasPair: true,
      previousDay,
      currentDay,
      range,
      label: range ? `Since previous crawl (${range})` : 'Since previous crawl'
    };
  }

  if (source.state === 'first-run' && source.currentRun) {
    return {
      state: 'first-run',
      hasPair: false,
      previousDay: null,
      currentDay,
      range: null,
      label: currentDay ? `First captured crawl on ${currentDay}` : 'First captured crawl'
    };
  }

  return null;
}

@Injectable({ providedIn: 'root' })
export class CrawlRunPeriodService {
  private readonly API_URL = environment.apiUrl + '/v1';

  /** Per-domain, so the Issues tab, its drawer and URL Detail share one request. */
  private readonly cache = new Map<string, Observable<CrawlRunPeriodPayload | null>>();

  constructor(private http: HttpClient) {}

  /**
   * A failed period read resolves to null — no label — rather than an error.
   * The header is context for the queue below it; it must never be the reason
   * an operator cannot see their Issues.
   */
  getForDomain(domain: string, options: { refresh?: boolean } = {}): Observable<CrawlRunPeriodPayload | null> {
    const key = (domain || '').trim().toLowerCase();
    if (!key) return of(null);

    if (options.refresh) this.cache.delete(key);

    const cached = this.cache.get(key);
    if (cached) return cached;

    const request = this.http
      .get<{ success: boolean; data: CrawlRunPeriodPayload | null }>(
        `${this.API_URL}/domains/${encodeURIComponent(key)}/crawl-run-period`
      )
      .pipe(
        map((res) => res?.data ?? null),
        catchError(() => of(null)),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.cache.set(key, request);
    return request;
  }

  /** Drop cached periods — a new capture turns the period over. */
  clear(domain?: string): void {
    const key = (domain || '').trim().toLowerCase();
    if (key) this.cache.delete(key);
    else this.cache.clear();
  }

  viewFor(domain: string, options: { refresh?: boolean } = {}): Observable<CrawlRunPeriodView | null> {
    return this.getForDomain(domain, options).pipe(map((payload) => buildCrawlRunPeriodView(payload)));
  }
}
